import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Badge } from "./Badge";
import "./PerformanceHero.css";

export type PerformanceZone = "below" | "neutral" | "above";
export type TrendDirection = "up" | "flat" | "down";

const ZONE_BADGE_TONE: Record<PerformanceZone, "danger" | "neutral" | "success"> = {
  below: "danger",
  neutral: "neutral",
  above: "success",
};

export interface PerformanceHeroProps {
  label: string;
  isLoading: boolean;
  /** Already-formatted percent string (e.g. "108,40%") — this component never formats/rounds, only presents. */
  percentDisplay: string | null;
  /** Shown instead of `percentDisplay` when it's null (no denominator) — caller-supplied so it's translatable. */
  unavailableLabel: string;
  zone: PerformanceZone | null;
  /** Caller-supplied localized zone label (e.g. from common.performanceZone) — shown in the badge next to the percent. */
  zoneBadgeLabel?: string;
  /** Already-formatted trend string (e.g. "+3,70 p.p." or "—") — shown only when `isLoading` is false. */
  trendDisplay?: string;
  trendDirection?: TrendDirection | null;
  /** Appended after the trend value when a real (non-null) trend exists, e.g. "u odnosu na prethodni period". */
  trendSuffix?: string;
  /**
   * Optional small muted caption under the hero — used for the "Učinak"
   * explanation ("računa se iz ostvarenog standardnog vremena…"), so the
   * standard-minutes intermediate stays available without being a KPI
   * (task: time-distribution simplification §6). Also set as the value's
   * `title` for a hover tooltip.
   */
  caption?: string;
}

function TrendIcon({ direction }: { direction: TrendDirection | null | undefined }) {
  if (direction === "up") return <TrendingUp size={14} aria-hidden="true" />;
  if (direction === "down") return <TrendingDown size={14} aria-hidden="true" />;
  return <Minus size={14} aria-hidden="true" />;
}

/**
 * The one shared "dominant performance number" primitive — used by both
 * Analytics (`PerformanceOverview`) and Dashboard's own hero
 * (docs/NORMAX_PERFORMANCE_DASHBOARD_SLICE_C_IMPLEMENTATION_REPORT.md),
 * extracted so both features render the exact same visual language rather
 * than two hand-maintained copies. Pure presentation only — no formatting,
 * no data fetching, no business logic; every value arrives pre-formatted
 * from the caller (`lib/format`'s `formatAnalyticsPercent`/`formatAnalyticsTrend`).
 *
 * The only bold visual choice is the thin zone-colored left edge — never a
 * flooded background (docs/NORMAX_PERFORMANCE_ANALYTICS_FRONTEND_SLICE_B_IMPLEMENTATION_REPORT.md
 * "Design rationale").
 */
export function PerformanceHero({
  label,
  isLoading,
  percentDisplay,
  unavailableLabel,
  zone,
  zoneBadgeLabel,
  trendDisplay,
  trendDirection,
  trendSuffix,
  caption,
}: PerformanceHeroProps) {
  const zoneClass = zone ?? "neutral";

  return (
    <div className={`nx-performance-hero nx-performance-hero--${zoneClass}`}>
      <p className="nx-performance-hero__label">{label}</p>
      {isLoading ? (
        <span className="nx-skeleton" style={{ width: "140px", height: "44px" }} />
      ) : percentDisplay ? (
        <p className="nx-performance-hero__value" title={caption}>{percentDisplay}</p>
      ) : (
        <p className="nx-performance-hero__value nx-performance-hero__value--unavailable">{unavailableLabel}</p>
      )}
      {!isLoading && caption ? <p className="nx-performance-hero__caption">{caption}</p> : null}
      <div className="nx-performance-hero__meta">
        {!isLoading && zone && zoneBadgeLabel ? <Badge tone={ZONE_BADGE_TONE[zone]}>{zoneBadgeLabel}</Badge> : null}
        {!isLoading && trendDisplay ? (
          <span className={`nx-performance-hero__trend nx-performance-hero__trend--${trendDirection ?? "unavailable"}`}>
            <TrendIcon direction={trendDirection} />
            {trendDisplay}
            {trendDirection && trendSuffix ? ` ${trendSuffix}` : ""}
          </span>
        ) : null}
      </div>
    </div>
  );
}
